import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { ProLinks } from "./ProLinks";

const BIO_A = [
  "Hi, I\u2019m Freya, founder of Digital AdDoctor.",
  "I\u2019ve spent over 18 years helping businesses grow through Google Ads, Meta Ads, SEO, analytics and conversion optimisation.",
  "With training in psychology, NLP and behavioural science, I bring together an understanding of human behaviour, data and creative thinking to develop digital marketing strategies that connect with customers.",
];
const BIO_B = [
  "I started Digital AdDoctor because I wanted to offer businesses something different: expert digital marketing with a genuinely personal approach.",
  "No confusing jargon, unnecessary complications or being passed between account managers. When you work with Digital AdDoctor, you work directly with me.",
  "I take the time to understand your business, your customers and your goals, creating strategies designed to deliver meaningful results.",
];

export const FounderSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <section ref={ref} className="overflow-hidden bg-white py-16 sm:py-28" data-testid="founder-section">
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <Reveal className="relative lg:sticky lg:top-28">
          <div className="relative mx-auto aspect-[4/5] max-w-sm overflow-hidden rounded-t-[999px] rounded-b-[32px] bg-[#EDE9FE] sm:max-w-md">
            <motion.img style={{ y }} src="/img/freya-98.jpg" alt="Freya Jones smiling, leaning on a velvet armchair" loading="lazy" className="h-[112%] w-full object-cover object-top" />
          </div>
          <div className="absolute -bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-[#13052A] px-5 py-3 text-sm font-bold text-white shadow-xl">
            <span className="h-2 w-2 rounded-full bg-[#FF7043]" /> 18+ years in digital marketing
          </div>
        </Reveal>
        <div className="pt-4 lg:pt-0">
          <Reveal>
            <p className="eyebrow text-[#7C3AED]"><span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" />Meet Freya</p>
            <h2 className="mt-5 text-3xl font-extrabold leading-[1.05] text-[#13052A] sm:text-4xl lg:text-5xl">
              Meet Freya: <span className="serif-accent font-normal text-[#581C87]">The Human Behind Digital AdDoctor</span>
            </h2>
          </Reveal>
          <Reveal delay={0.08} className="mt-7 space-y-4 text-base leading-relaxed text-slate-600 md:text-lg">
            {BIO_A.map((p, i) => <p key={i} className={i === 0 ? "text-xl font-bold text-[#13052A]" : ""}>{p}</p>)}
          </Reveal>
          <Reveal delay={0.1} className="mt-6 space-y-4 rounded-[24px] bg-[#F5F0FF] p-6 text-base leading-relaxed text-slate-700 sm:p-7">
            {BIO_B.map((p, i) => <p key={i}>{p}</p>)}
          </Reveal>
          <Reveal delay={0.12}>
            <p className="serif-accent mt-6 text-2xl text-[#581C87]">Because when your business grows, we grow together.</p>
            <Link to="/about-freya-jones" className="btn-primary mt-7" data-testid="founder-about-cta">Find Out More About Freya <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </div>
      <ProLinks />
    </section>
  );
};
